/**
 * Project-specific custom error processors for jupiter.
 *
 * Registered with the global `errorProcessors` registry. Imported (for
 * side effects) from `src/store/init.ts` at app boot.
 */

import { errorProcessors } from "./errorProcessors";
import type { ErrorProcessor } from "./errorProcessors";
import { resolveEntityFieldLabel } from "../common/utils/resolveEntityFieldLabel";

// ENTITY_LOCKED_BY_EDITION_DOCUMENTATION:
// Renderiza un mensaje contextual con:
// - Lista de campos protegidos que el usuario intentó modificar (con labels).
//   En un borrado (meta.operation === "delete") no hay campos: se dice que no
//   se puede borrar.
// - Lista de ediciones afectadas, cada una con su estado y marca ⚠ si tiene
//   firmas en proceso (sentForSignature / falloDeFirmas).
// - Acciones requeridas: revertir documentación, y cancelar firmas si aplica.
errorProcessors.register(
  "ENTITY_LOCKED_BY_EDITION_DOCUMENTATION",
  (meta, context) => {
    const { langStore, language } = context;
    const m = meta as Record<string, any>;
    const entityType: string = m.entityType || "";
    const changedFields: string[] = Array.isArray(m.changedFields) ? m.changedFields : [];
    const signaturesInFlight: boolean = !!m.signaturesInFlight;
    const items: any[] = Array.isArray(m.items) ? m.items : [];
    const isDelete = m.operation === "delete";

    const fieldLabels = changedFields.map(f => resolveEntityFieldLabel(langStore, entityType, f));

    const lang = language as "es" | "en" | "pt";
    const t = <T extends Record<"es" | "en" | "pt", string>>(o: T): string => o[lang];

    const parts: string[] = [];

    if (!isDelete && fieldLabels.length > 0) {
      parts.push(
        t({
          es: "Campos protegidos que intentaste modificar:",
          en: "Protected fields you tried to modify:",
          pt: "Campos protegidos que você tentou modificar:",
        }),
      );
      for (const f of fieldLabels) parts.push(`  • ${f}`);
      parts.push("");
    }

    if (items.length > 0) {
      parts.push(
        isDelete
          ? t({
              es: "No se puede borrar: integra la documentación en circulación de estas ediciones.",
              en: "It cannot be deleted: it is part of the documentation in flight of these editions.",
              pt: "Não é possível excluir: integra a documentação em circulação destas edições.",
            })
          : t({
              es: "Ediciones afectadas:",
              en: "Affected editions:",
              pt: "Edições afetadas:",
            }),
      );
      for (const it of items) {
        const editionName: string = it.editionName || it.id || "";
        const stateText: string = it.stateLabel?.[lang] || it.state || "";
        const warn = it.signaturesInFlight ? "  ⚠" : "";
        parts.push(`  • ${editionName} — ${stateText}${warn}`);
      }
      parts.push("");
    }

    // Acción: el usuario debe ir a cada edición y ejecutar la transición
    // "Borrar Documentación", que revierte el estado del workflow (y como
    // efecto: borra los documentos generados, y anula firmas si las hay).
    parts.push(
      isDelete
        ? t({
            es: "Para borrarlo, borrá primero la documentación de cada edición y volvé a intentarlo.",
            en: "To delete it, first delete the documentation of each edition and try again.",
            pt: "Para excluí-lo, exclua primeiro a documentação de cada edição e tente novamente.",
          })
        : t({
            es: "No es posible modificar los datos de una edición que contenga documentación previamente generada. Para realizar estos cambios, borrá la documentación existente y volvé a intentarlo.",
            en: "The data of an edition that already has generated documentation cannot be modified. To make these changes, delete the existing documentation and try again.",
            pt: "Não é possível modificar os dados de uma edição que contenha documentação previamente gerada. Para realizar estas alterações, exclua a documentação existente e tente novamente.",
          }),
    );
    if (signaturesInFlight) {
      parts.push(
        t({
          es: "En las ediciones cuyo estado indica que ya están con firmas en proceso (marcadas con ⚠), esa misma acción también anulará el proceso de firma.",
          en: "For editions whose state indicates signatures in flight (marked with ⚠), that same action will also cancel the signature process.",
          pt: "Nas edições cujo estado indica assinaturas em andamento (marcadas com ⚠), essa mesma ação também cancelará o processo de assinatura.",
        }),
      );
    }

    return parts.join("\n");
  },
);

// EDITION_LYRICS_INCONSISTENT (KAN-448): al generar la documentación, la letra
// de una obra no coincide con el porcentaje de letra de sus autores. El server
// manda cada obra (título en `label`) con su caso en `issue`; el renglón se
// arma acá con los textos de LangStoreCustom. Un caso desconocido muestra la
// obra sola.
errorProcessors.register("EDITION_LYRICS_INCONSISTENT", (meta, context) => {
  const { langStore } = context;
  const items: any[] = Array.isArray(meta.items) ? meta.items : [];
  const templates: Record<string, string | undefined> = {
    LYRICS_WITHOUT_LYRICISTS: langStore.editionLyricsWithoutLyricists,
    LYRICISTS_WITHOUT_LYRICS: langStore.editionLyricistsWithoutLyrics,
  };
  const field = resolveEntityFieldLabel(langStore, "MusicalWork", "originalLyrics");
  return items
    .map((item) => {
      const title = String(item.label ?? "");
      const template = templates[String(item.issue)];
      // Con función de reemplazo: un título con "$&" o "{field}" se muestra
      // tal cual.
      const line = template
        ? template.replace("{field}", () => field).replace("{title}", () => title)
        : title;
      return `• ${line}`;
    })
    .join("\n");
});

// EDITION_AUTHORS_INCOMPLETE y AUTHOR_DATA_INCOMPLETE (KAN-450): autores a los
// que les faltan datos personales, al generar la documentación (también en el
// registro DNDA y el boletín) o al pedir la constancia de CUIL a mano. El server
// manda cada autor (nombre en `label`) con los campos que faltan por nombre
// (`missingFields`); acá se ponen las etiquetas y se arma el renglón.
const autoresIncompletos: ErrorProcessor = (meta, context) => {
  const { langStore } = context;
  const items: any[] = Array.isArray(meta.items) ? meta.items : [];
  return items
    .map((item) => {
      const autor = String(item.label ?? "");
      const campos: string[] = Array.isArray(item.missingFields) ? item.missingFields : [];
      if (campos.length === 0) return `• ${autor}`;
      const etiquetas = campos.map((f) => resolveEntityFieldLabel(langStore, "AuthorComposer", f)).join(", ");
      const plantilla =
        campos.length === 1 ? langStore.editionAuthorMissingOne : langStore.editionAuthorMissingMany;
      // Con función de reemplazo y el autor al final: un nombre con "$&" o
      // "{fields}" se muestra tal cual.
      return `• ${plantilla.replace("{fields}", () => etiquetas).replace("{author}", () => autor)}`;
    })
    .join("\n");
};
errorProcessors.register("EDITION_AUTHORS_INCOMPLETE", autoresIncompletos);
errorProcessors.register("AUTHOR_DATA_INCOMPLETE", autoresIncompletos);

// AUTHOR_TAX_ID_INVALID (KAN-450): el server lo devuelve si se pide a mano la
// constancia de CUIL y el tipo de documento del autor no es CUIT/CUIL ni DU, o
// el número no tiene la forma esperada. El pedido a mano era la mutación
// downloadAuthorCuil, que desde el hotfix de la capa segura no está en la API
// (el pipeline lo lanza solo el server), y nada del cliente la usaba: el texto
// queda como defensa, por si vuelve ese camino. El server manda el autor (nombre en `label`) y el dato (`field`:
// taxId o taxIdType), y un mensaje en castellano que acá no se usa: el renglón
// lleva la etiqueta del dato.
errorProcessors.register("AUTHOR_TAX_ID_INVALID", (meta, context) => {
  const { langStore } = context;
  const items: any[] = Array.isArray(meta.items) ? meta.items : [];
  return items
    .map((item) => {
      const autor = String(item.label ?? "");
      const campo = typeof item.field === "string" && item.field !== "" ? item.field : null;
      return campo ? `• ${autor}: ${resolveEntityFieldLabel(langStore, "AuthorComposer", campo)}` : `• ${autor}`;
    })
    .join("\n");
});
